/**
 * GroundedSearchBox — Gemini-powered RAG search for the Instant Pricing page.
 *
 * UX rules baked in:
 *   • Sits in its own segment ABOVE the existing per-category vendor lists —
 *     never replaces or filters them.
 *   • Surfaces ONLY a natural-language answer (≤2 sentences) + up to 6
 *     clickable vendor cards. Cards link to the vendor profile; no contact
 *     info is shown here per platform rules.
 *   • Out-of-scope queries get a polite refusal directly from the backend
 *     system prompt; we render whatever Gemini returned.
 *   • Sends current event_type + city as filters so Gemini's context is
 *     pre-narrowed (also tells the model what the customer is shopping for).
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlass, Sparkle, Spinner, MapPin, Star, ArrowRight, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_QUERIES = [
  'Find me wedding photographers under ₹50k',
  'What\'s the cheapest catering plate available',
  'Decorators with great reviews',
  'Show me venues that fit a small reception',
];

const GroundedSearchBox = ({ eventType = '', city = '' }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // null while loading settings, then {enabled, limit, used, remaining,
  // is_authenticated}. If !enabled the component renders nothing.
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/search/settings`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setSettings(data); })
      .catch(() => { if (!cancelled) setSettings({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  const runSearch = async (q) => {
    const text = (q ?? query).trim();
    if (!text || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/search/grounded`, {
        query: text,
        event_type: eventType || undefined,
        city: city || undefined,
      }, { withCredentials: true });
      setResult(data);
      // Update local quota from the response (the server already incremented it).
      if (data?.rate_limit?.limit != null) {
        setSettings((s) => ({
          ...(s || {}),
          limit: data.rate_limit.limit,
          used: data.rate_limit.used,
          remaining: data.rate_limit.remaining,
        }));
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Search assistant unavailable';
      if (status === 429) {
        // Reflect the exhausted quota locally so the UI updates instantly.
        setSettings((s) => ({ ...(s || {}), used: s?.limit ?? null, remaining: 0 }));
      }
      toast.error(detail);
    }
    setLoading(false);
  };

  const clear = () => {
    setQuery('');
    setResult(null);
  };

  const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

  // Don't render anything while we're loading settings, or if admin disabled
  // the AI search entirely. Keeps the per-category listings the visual focus.
  if (!settings || settings.enabled === false) return null;

  // Quota indicators
  const hasLimit = settings.limit != null && Number(settings.limit) > 0;
  const remaining = Number(settings.remaining ?? settings.limit ?? 0);
  const quotaExhausted = hasLimit && remaining <= 0;

  return (
    <section className="border border-[#E8E1D5] bg-gradient-to-br from-sand-50 to-white"
      data-testid="grounded-search-section">
      <div className="px-5 py-4 border-b border-[#E8E1D5] flex items-center gap-3">
        <Sparkle size={18} weight="fill" className="text-brand-primary" />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-primary">
            AI Vendor Concierge
          </div>
          <div className="text-xs text-text-secondary">
            Powered by Gemini — answers strictly from our verified listings.{' '}
            <span className="hidden sm:inline">No outside info, no contact details shared.</span>
          </div>
        </div>
        {hasLimit && (
          <div className="text-right shrink-0" data-testid="grounded-search-quota">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${
              quotaExhausted ? 'text-red-600' : 'text-text-secondary'
            }`}>
              {remaining}/{settings.limit} left today
            </div>
            <div className="text-[9px] text-text-secondary">
              {settings.is_authenticated ? 'per-account quota' : 'per-IP quota — log in for personal limit'}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !quotaExhausted) { e.preventDefault(); runSearch(); } }}
              placeholder={quotaExhausted
                ? `Daily limit reached — try again tomorrow`
                : `Ask anything${city ? ` about vendors in ${city}` : ''}…`}
              className="h-11 pl-9 rounded-none border-[#E8E1D5] focus:ring-brand-primary"
              disabled={quotaExhausted}
              data-testid="grounded-search-input"
            />
          </div>
          <Button onClick={() => runSearch()} disabled={loading || !query.trim() || quotaExhausted}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 h-11 px-5 disabled:opacity-60"
            data-testid="grounded-search-submit">
            {loading ? <Spinner size={14} className="mr-2 animate-spin" /> : <ArrowRight size={14} className="mr-2" weight="bold" />}
            Search
          </Button>
        </div>

        {quotaExhausted && (
          <div className="mt-3 px-3 py-2 border border-red-200 bg-red-50 text-xs text-red-700 flex items-center gap-2"
            data-testid="grounded-search-limit-banner">
            <X size={14} weight="bold" />
            Daily search limit reached ({settings.limit}/{settings.limit}). Try again tomorrow.
          </div>
        )}

        {/* Suggested queries — only when there's no answer yet AND quota left. */}
        {!result && !loading && !quotaExhausted && (
          <div className="flex flex-wrap gap-2 mt-3">
            {SUGGESTED_QUERIES.map((s) => (
              <button key={s} onClick={() => { setQuery(s); runSearch(s); }}
                className="text-[11px] px-3 py-1 border border-[#E8E1D5] hover:border-brand-primary hover:text-brand-primary transition-colors"
                data-testid={`grounded-search-suggestion-${s.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="mt-4 flex items-center gap-3 text-sm text-text-secondary">
            <Spinner size={16} className="animate-spin" />
            Searching our verified listings…
          </div>
        )}

        {result && !loading && (
          <div className="mt-4 space-y-3" data-testid="grounded-search-result">
            <div className="bg-white border border-[#E8E1D5] p-4 relative">
              <button onClick={clear}
                className="absolute top-2 right-2 text-text-secondary hover:text-brand-primary"
                data-testid="grounded-search-clear">
                <X size={14} />
              </button>
              <div className="flex items-start gap-2">
                <Sparkle size={14} weight="fill" className="text-brand-primary mt-0.5 shrink-0" />
                <p className="text-sm text-text-primary leading-relaxed pr-6"
                  data-testid="grounded-search-answer">{result.answer}</p>
              </div>
              {result.fallback_reason && (
                <p className="text-[11px] text-text-secondary mt-2 pl-6 italic"
                  data-testid="grounded-search-fallback">
                  {result.fallback_reason}
                </p>
              )}
            </div>

            {result.recommended_vendors?.length > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-text-secondary mb-2">
                  Recommended ({result.recommended_vendors.length})
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {result.recommended_vendors.map((v) => (
                    <Link key={v.id} to={`/vendors/${v.id}`}
                      className="bg-white border border-[#E8E1D5] hover:border-brand-primary transition-colors"
                      data-testid={`grounded-search-vendor-${v.id}`}>
                      <div className="aspect-[4/3] bg-sand-100 overflow-hidden">
                        {v.thumbnails?.[0] ? (
                          <img src={fullUrl(v.thumbnails[0])} alt={v.name}
                            className="w-full h-full object-cover" loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[11px] text-text-secondary">
                            No photo
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-heading text-text-primary truncate">{v.name}</div>
                        <div className="text-[11px] text-text-secondary mt-1 flex items-center gap-2">
                          {v.city && (
                            <span className="flex items-center gap-1">
                              <MapPin size={11} weight="fill" /> {v.city}
                            </span>
                          )}
                          {v.rating > 0 && (
                            <span className="flex items-center gap-1 text-amber-700">
                              <Star size={11} weight="fill" /> {Number(v.rating).toFixed(1)}
                              <span className="text-text-secondary">({v.rating_count})</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <p className="text-[10px] text-text-secondary mt-2 italic">
                  Tap any card to view full details, packages and pricing on the vendor's profile.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default GroundedSearchBox;
